import type { ApexOptions } from 'apexcharts';
import type { BarChartComponent } from '../types';

import React, { lazy, Suspense } from 'react';
import { buildAxis, chartCardSx, chartTitleSx, chartPalette, baseChartOptions } from '../chart-style';

import { Box, Card, useTheme, Typography, useMediaQuery } from '@mui/material';

const LazyApex = lazy(() => import('react-apexcharts'));

export interface BarChartProps {
  component: BarChartComponent;
}

export function BarChart({ component }: BarChartProps): React.JSX.Element {
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const horizontal = component.orientation === 'horizontal';
  const stacked = component.stacked ?? false;
  const palette = chartPalette(theme);
  const colors = component.series.length === 1 ? [palette[0]] : palette;

  const base = baseChartOptions(theme, reduceMotion);
  const options: ApexOptions = {
    ...base,
    chart: { ...base.chart, type: 'bar', stacked },
    colors,
    plotOptions: {
      bar: { horizontal, columnWidth: '56%', borderRadius: 4 },
    },
    fill: { type: 'solid', opacity: 1, colors },
    stroke: { show: false },
    xaxis: buildAxis(theme, {
      categories: component.categories,
      label: component.xLabel,
    }),
    yaxis: buildAxis(theme, {
      label: component.yLabel,
      forceNiceScale: true,
    }),
    legend: {
      show: component.series.length > 1,
      labels: { colors: theme.palette.common.white },
      fontFamily: theme.typography.fontFamily as string,
      fontWeight: 500,
      itemMargin: { horizontal: 12, vertical: 4 },
      markers: { size: 6, strokeWidth: 0 },
    },
  };

  return (
    <Card data-testid="ari.bot-ui.bar-chart" elevation={0} sx={chartCardSx(theme)}>
      {component.title && (
        <Typography variant="h6" sx={chartTitleSx(theme)}>
          {component.title}
        </Typography>
      )}
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, position: 'relative' }}>
        <Suspense fallback={null}>
          <LazyApex type="bar" series={component.series} options={options} width="100%" height="100%" />
        </Suspense>
      </Box>
    </Card>
  );
}
